import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Mail, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Power, ExternalLink } from 'lucide-react';

export default function GmailIntegrationCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [logs, setLogs] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/gmail/status');
      setStatus(data);
    } catch (e) {
      toast.error('Erreur statut Gmail');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await api.get('/gmail/recent-activity?limit=15');
      setLogs(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchLogs();
    // detect callback return ?gmail=connected
    const u = new URL(window.location.href);
    if (u.searchParams.get('gmail') === 'connected') {
      toast.success('Gmail connecté avec succès ✉️');
      u.searchParams.delete('gmail');
      window.history.replaceState({}, '', u.toString());
    }
  }, [fetchStatus, fetchLogs]);

  const connect = async () => {
    try {
      const { data } = await api.get('/oauth/gmail/login');
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.error('URL OAuth manquante');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur connexion Gmail');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Déconnecter Gmail de ProdPilot ? Le scan automatique sera arrêté.')) return;
    try {
      await api.post('/gmail/disconnect');
      toast.success('Gmail déconnecté');
      fetchStatus();
    } catch (e) {
      toast.error('Erreur déconnexion');
    }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post('/gmail/scan-now');
      if (data?.ok) {
        toast.success(`Scan terminé · ${data.processed} email(s) traité(s) · ${data.new_replies_matched} réponse(s) · ${data.new_price_requests} demande(s) de prix`);
      } else {
        toast.error(data?.reason || 'Scan échoué');
      }
      fetchStatus();
      fetchLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur scan');
    } finally {
      setScanning(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      await api.put('/gmail/settings', { [key]: value });
      fetchStatus();
    } catch {
      toast.error('Erreur sauvegarde');
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 flex items-center gap-3 text-zinc-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement Gmail...
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden" data-testid="gmail-card">
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-3">
        <div className="w-9 h-9 rounded-sm bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shrink-0 shadow shadow-rose-500/20">
          <Mail className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            Boîte Gmail
            {status?.connected ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-1.5 py-0.5" data-testid="gmail-connected-badge">
                <CheckCircle2 className="w-3 h-3" /> Connectée
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-800 border border-zinc-700 rounded-sm px-1.5 py-0.5">
                <Power className="w-3 h-3" /> Déconnectée
              </span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 truncate">
            {status?.profile_email
              ? `Compte : ${status.profile_email}`
              : "Connecte ta boîte Gmail pour automatiser les réponses prospects et l'ingestion des demandes de prix."}
          </div>
        </div>
        {status?.connected ? (
          <>
            <Button size="sm" variant="outline" onClick={scanNow} disabled={scanning}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8" data-testid="gmail-scan-now-btn">
              {scanning ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
              Scanner maintenant
            </Button>
            <Button size="sm" variant="outline" onClick={disconnect}
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-8" data-testid="gmail-disconnect-btn">
              <Power className="w-3 h-3 mr-2" /> Déconnecter
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={connect}
            className="bg-rose-500 hover:bg-rose-600 text-white h-8" data-testid="gmail-connect-btn">
            <ExternalLink className="w-3 h-3 mr-2" /> Connecter Gmail
          </Button>
        )}
      </div>

      {status?.connected && (
        <>
          {/* Settings */}
          <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-3 border-b border-zinc-800">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Intervalle de scan</div>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number" min={5} max={60}
                  value={status.scan_interval_minutes}
                  onChange={(e) => setStatus(s => ({ ...s, scan_interval_minutes: Number(e.target.value) }))}
                  onBlur={(e) => updateSetting('scan_interval_minutes', Number(e.target.value))}
                  className="bg-zinc-900 border-zinc-800 h-8 text-sm w-20"
                  data-testid="gmail-interval-input"
                />
                <span className="text-xs text-zinc-500">min</span>
              </div>
              <div className="text-[10px] text-zinc-600 mt-1">Effectif au prochain redémarrage</div>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm text-zinc-200">Brouillons IA réponses</div>
                <div className="text-[10px] text-zinc-500">Génère un brouillon quand un prospect répond</div>
              </div>
              <Switch
                checked={!!status.auto_draft_replies}
                onCheckedChange={(v) => updateSetting('auto_draft_replies', v)}
                data-testid="gmail-toggle-drafts"
              />
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm text-zinc-200">Demandes de prix auto</div>
                <div className="text-[10px] text-zinc-500">Crée une PR si email avec PDF</div>
              </div>
              <Switch
                checked={!!status.auto_create_price_requests}
                onCheckedChange={(v) => updateSetting('auto_create_price_requests', v)}
                data-testid="gmail-toggle-pr"
              />
            </div>
          </div>

          {/* Last scan + activity */}
          <div className="px-5 py-3 border-b border-zinc-800 text-[11px] text-zinc-500 flex flex-wrap items-center gap-3" data-testid="gmail-last-scan">
            {status.last_scan_at ? (
              <>
                <span>Dernier scan : <strong className="text-zinc-300">{new Date(status.last_scan_at).toLocaleString('fr-FR')}</strong></span>
                <span>·</span>
                <span>{status.last_scan_processed || 0} traité(s)</span>
                {status.last_scan_skipped_reason && (
                  <span className="text-amber-400 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {status.last_scan_skipped_reason}
                  </span>
                )}
              </>
            ) : (
              <span>Aucun scan effectué pour l'instant</span>
            )}
          </div>

          {/* Recent activity */}
          <div className="px-5 py-3" data-testid="gmail-activity">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Activité récente</div>
            <div className="max-h-72 overflow-y-auto divide-y divide-zinc-800/60">
              {logs.length === 0 ? (
                <div className="text-xs text-zinc-500 py-4 text-center">Aucune activité pour l'instant</div>
              ) : logs.map(l => (
                <div key={l.id} className="py-2 flex items-start gap-2 text-xs">
                  <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                    l.kind === 'ai_reply_match' ? 'bg-emerald-400'
                    : l.kind === 'inbound_price_request' ? 'bg-amber-400'
                    : 'bg-zinc-600'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-200 truncate font-medium">{l.from_name || l.from_email || 'Inconnu'}</span>
                      <span className="text-[10px] text-zinc-500 truncate">{l.from_email}</span>
                      <span className="text-[10px] text-zinc-600 ml-auto whitespace-nowrap">{new Date(l.created_at).toLocaleString('fr-FR')}</span>
                    </div>
                    <div className="text-zinc-400 truncate">{l.subject || '(sans objet)'}</div>
                    {l.kind === 'ai_reply_match' && (
                      <div className="text-[10px] text-emerald-400 mt-0.5">
                        ⤷ Réponse {l.intent === 'positive_reply' ? 'positive' : l.intent === 'negative_reply' ? 'négative' : '(neutre)'} de {l.company_name}
                      </div>
                    )}
                    {l.kind === 'inbound_price_request' && (
                      <div className="text-[10px] text-amber-400 mt-0.5">
                        ⤷ Demande de prix créée {l.attachments_count ? `· ${l.attachments_count} PJ` : ''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
